import { authService } from '../services/authService'
import { listOpenEvents } from '../services/predictionService'

const REFRESH_MS = 60_000

export type PredictionEventsUpdatedDetail = {
  action?: string
  userEntryVisible?: boolean
  openEventCount?: number
  userVisible?: boolean
  tenantId?: string
  timestamp?: string
}

type Snapshot = {
  userEntryVisible: boolean
  hasOpenEvents: boolean
  checked: boolean
}

type Listener = () => void

const listeners = new Set<Listener>()
let userEntryVisible = true
let hasOpenEvents = false
let checked = false
let snapshot: Snapshot = { userEntryVisible, hasOpenEvents, checked }
let pollTimer: ReturnType<typeof setInterval> | undefined
let wsBound = false
let activePredictionEnabled = false
let fetchGeneration = 0
let syncRefCount = 0

function publishSnapshotIfChanged() {
  if (
    snapshot.userEntryVisible === userEntryVisible
    && snapshot.hasOpenEvents === hasOpenEvents
    && snapshot.checked === checked
  ) {
    return
  }
  snapshot = { userEntryVisible, hasOpenEvents, checked }
  listeners.forEach((listener) => listener())
}

async function fetchOpenEvents(force = false) {
  const generation = ++fetchGeneration
  if (!activePredictionEnabled || !authService.isAuthenticated()) {
    hasOpenEvents = false
    checked = true
    publishSnapshotIfChanged()
    return
  }

  try {
    const res = await listOpenEvents(force)
    if (generation !== fetchGeneration) return
    if (typeof res.meta?.userEntryVisible === 'boolean') {
      userEntryVisible = res.meta.userEntryVisible
    }
    hasOpenEvents = (res.data?.length ?? 0) > 0
  } catch {
    if (generation !== fetchGeneration) return
    if (!force) hasOpenEvents = false
  } finally {
    if (generation === fetchGeneration) {
      checked = true
      publishSnapshotIfChanged()
    }
  }
}

async function fetchOpenEventsWithRetry(force = true) {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    await fetchOpenEvents(force)
    if (hasOpenEvents) return
    if (attempt < 3) {
      await new Promise((resolve) => window.setTimeout(resolve, 350 * (attempt + 1)))
    }
  }
}

function handlePredictionEventsUpdated(event: Event) {
  const detail = (event as CustomEvent<PredictionEventsUpdatedDetail>).detail
  if (detail?.action === 'user_entry' && typeof detail.userEntryVisible === 'boolean') {
    userEntryVisible = detail.userEntryVisible
    if (!detail.userEntryVisible) {
      hasOpenEvents = false
      checked = true
      publishSnapshotIfChanged()
      return
    }
    if (typeof detail.openEventCount === 'number') {
      hasOpenEvents = detail.openEventCount > 0
      checked = true
      publishSnapshotIfChanged()
    }
    void fetchOpenEventsWithRetry(true)
    return
  }
  void fetchOpenEventsWithRetry(true)
}

function bindWebSocketListenerOnce() {
  if (wsBound) return
  wsBound = true
  window.addEventListener('prediction_events_updated', handlePredictionEventsUpdated)
}

export function subscribePredictionEntry(listener: Listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getPredictionEntrySnapshot(): Snapshot {
  return snapshot
}

export function refreshPredictionEntry(force = true) {
  void fetchOpenEventsWithRetry(force)
}

export function startPredictionEntrySync(predictionEnabled?: boolean) {
  bindWebSocketListenerOnce()
  activePredictionEnabled = predictionEnabled === true
  syncRefCount += 1

  if (syncRefCount === 1) {
    checked = false
    publishSnapshotIfChanged()
    void fetchOpenEvents(false)
    pollTimer = setInterval(() => {
      void fetchOpenEvents(false)
    }, REFRESH_MS)
  }

  return () => {
    syncRefCount = Math.max(0, syncRefCount - 1)
    if (syncRefCount === 0 && pollTimer) {
      clearInterval(pollTimer)
      pollTimer = undefined
    }
  }
}
